import { TransformationInterface } from '@simOn/common/matterport';

/**
 * Actions suggested to the user alongside an assistant message (`AgentActionDto` in swagger).
 * `type` is the discriminator; each derived type carries its own payload. The user triggers an action
 * from a card in the chat and `AgentService` dispatches it to a concrete sub-service.
 *
 * Swagger exposes only the base type; the payloads below follow `tasks/AI-Frontend-Handoff.md`.
 */

/** Numeric action type (`AgentActionType` in swagger); the API serializes it by name. */
export enum AgentActionType {
  Unknown = 0,
  NavigateToAsset = 1,
  OpenDocument = 2,
  NavigateToTicket = 3,
  NavigateToEvent = 4,
  CreateAsset = 5,
  UpdateAsset = 6,
  CreateTicket = 7,
  UpdateTicket = 8,
  UpdateTicketStatus = 9,
  CreateEvent = 10,
  UpdateEvent = 11
}

/** Fields shared by every action. */
export interface AgentActionBaseDto {
  id: string;
  /** Declared as a number in swagger; tolerated as the enum name too (e.g. "NavigateToAsset"). */
  type: AgentActionType | string;
}

/**
 * Shared shape of every `NavigateTo*` action: everything needed to reach the target
 * (apartment -> room -> 3D position) without an extra request; the target itself is opened by type.
 */
export interface NavigateToActionBaseDto extends AgentActionBaseDto {
  targetId: string;
  targetName: string | null;
  apartmentId: string;
  apartmentName: string | null;
  /** `null` when the target is not assigned to a room (tickets and events). */
  roomId: string | null;
  roomName: string | null;
  /** `false` when the target has no usable 3D position: navigate without focusing the camera. */
  hasPosition: boolean;
  transformation: TransformationInterface | null;
}

/** Opens the asset details (and, in the 3D walk, lets the user jump to its position). */
export interface NavigateToAssetActionDto extends NavigateToActionBaseDto {
  scanProviderType: string | null;
  scanModelId: string | null;
  scanTagId: string | null;
}

export type AgentTicketStatus = 'Unknown' | 'Open' | 'InProgress' | 'Closed';

/** Opens the ticket (task) preview. */
export interface NavigateToTicketActionDto extends NavigateToActionBaseDto {
  assetId: string | null;
  dueDate: string | null;
  status: AgentTicketStatus;
}

/** Opens the calendar event preview. */
export interface NavigateToEventActionDto extends NavigateToActionBaseDto {
  startDate: string | null;
  endDate: string | null;
  /** "HH:mm:ss" */
  startTime: string | null;
  endTime: string | null;
  isAllDay: boolean;
}

/** Opens a document of the given space in the attachment preview; ids only, no blob paths. */
export interface OpenDocumentActionDto extends AgentActionBaseDto {
  apartmentId: string;
  documentId: string;
  directoryId: string | null;
  fileName: string | null;
  documentType: string;
}

/** Asset reminder (`AssetEventDto`); `id` is `null` for a new one. */
export interface AgentAssetReminder {
  id: string | null;
  /** "2026-11-05T09:00:00Z" (UTC) */
  dateTime: string;
  content: string;
  /** `null` = once */
  recurringEventType: 'Day' | 'Week' | 'Month' | 'Year' | null;
}

/** Asset link (`AssetHyperlinkDto`); `id` is `null` for a new one. */
export interface AgentAssetHyperlink {
  id: string | null;
  /** Complete http(s) address. */
  hyperlink: string;
}

/** Same property names as the `CreateAsset` request body. */
export interface AgentAssetPayload {
  name: string;
  roomId: string;
  categoryId: string | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  textNote: string | null;
  purchaseDate: string | null;
  purchasePrice: number | null;
  purchaseCurrency: string | null;
  currentValue: number | null;
  currentCurrency: string | null;
  warrantyEnd: string | null;
  /**
   * Reminders of the asset. On an `UpdateAsset` card the list, when set, is the COMPLETE new list (an empty
   * array removes all); `null` means unchanged.
   */
  events: AgentAssetReminder[] | null;
  /** Links of the asset; same replace semantics as `events`. */
  hyperlinks: AgentAssetHyperlink[] | null;
}

export interface AgentSimilarResource {
  id: string;
  name: string | null;
}

/** Shared shape of every `Create*` draft card. Nothing is created until the user confirms. */
export interface CreateResourceActionBaseDto extends AgentActionBaseDto {
  apartmentId: string;
  apartmentName: string | null;
  roomId: string | null;
  roomName: string | null;
  /** Resources of the same room that may be duplicates of the draft. */
  similarResources: AgentSimilarResource[];
}

export interface CreateAssetActionDto extends CreateResourceActionBaseDto {
  categoryName: string | null;
  payload: AgentAssetPayload;
}

export interface AgentFieldChange {
  /** Payload property name. */
  field: string;
  oldValue: string | null;
  /** `null` means the value was removed. */
  newValue: string | null;
}

/** Shared shape of every `Update*` draft card. Nothing is changed until the user confirms. */
export interface UpdateResourceActionBaseDto extends AgentActionBaseDto {
  targetId: string;
  /** Current name. */
  targetName: string | null;
  apartmentId: string;
  apartmentName: string | null;
  /** Current room. */
  roomId: string | null;
  roomName: string | null;
  changes: AgentFieldChange[];
  /** Payload property names to set to `null`. */
  clearedFields: string[];
}

export interface UpdateAssetActionDto extends UpdateResourceActionBaseDto {
  /** Only CHANGED values are set; `null` / missing means "unchanged" (removed values are in `clearedFields`). */
  payload: Partial<AgentAssetPayload>;
}

/** Reminder offset before the due date / start of a ticket or event (`TicketNotificationTemplateDto` without id). */
export interface AgentReminder {
  offsetValue: number;
  offsetUnit: 'Minutes' | 'Hours' | 'Days' | 'Weeks';
}

/** `TaskType` enum names of tickets. */
export type AgentTicketTaskType =
  | 'Others'
  | 'Inspection'
  | 'Servicing'
  | 'RepairsAndFixes'
  | 'Upkeep'
  | 'Troubleshooting'
  | 'Monitoring'
  | 'SafetyChecks'
  | 'ComplianceChecks'
  | 'CleaningAndSanitization';

/** Same shape as the checklist input of `CreateTicket` / `UpdateTicket`: the template and all its items. */
export interface AgentTicketChecklistInput {
  sourceChecklistId: string;
  reloadChecklistFromTemplate: boolean;
  items: { id: string; isChecked: boolean }[];
}

/** Same property names as the `CreateTicket` request body. Dates and times are UTC. */
export interface AgentTicketPayload {
  name: string | null;
  /** Safe HTML built by the backend (`<p>` only). */
  htmlText: string | null;
  /** "2026-11-05" */
  dueDate: string | null;
  /** "10:00:00" */
  dueTime: string | null;
  roomId: string | null;
  assetId: string | null;
  taskType: AgentTicketTaskType | null;
  /** Always exactly one id when set. */
  assigneeIds: string[] | null;
  notificationTemplates: AgentReminder[] | null;
  contractorId: string | null;
  checklist: AgentTicketChecklistInput | null;
}

/** A user referenced by a card, for display. */
export interface AgentUserRef {
  id: string;
  name: string | null;
}

export interface CreateTicketActionDto extends CreateResourceActionBaseDto {
  /** Linked asset, for display. */
  assetName: string | null;
  /** The assignee, for display. */
  assignees: AgentUserRef[];
  contractorName: string | null;
  checklistTitle: string | null;
  /** Complete create body. */
  payload: AgentTicketPayload;
}

export interface UpdateTicketActionDto extends UpdateResourceActionBaseDto {
  /** Only CHANGED values are set; `null` / missing means "unchanged" (removed values are in `clearedFields`). */
  payload: Partial<AgentTicketPayload>;
}

export type AgentTicketStatusChange = 'Open' | 'InProgress' | 'Closed';

export interface UpdateTicketStatusActionDto extends UpdateResourceActionBaseDto {
  /** Complete `UpdateTicketStatus` body. */
  payload: { ticketId: string; status: AgentTicketStatusChange };
}

export type AgentEventRecurrenceType = 'None' | 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

/** Same property names as the `CreateEvent` request body. Dates and times are UTC. */
export interface AgentEventPayload {
  name: string | null;
  description: string | null;
  /** "2026-11-05T00:00:00Z" (UTC day) */
  startDate: string | null;
  /** Last day of a multi-day all-day event. */
  endDate: string | null;
  isAllDay: boolean | null;
  /** "10:00:00"; `null` for all-day events. */
  startTime: string | null;
  endTime: string | null;
  roomId: string | null;
  /** `false` = visible only to `assigneeIds`. */
  isOpen: boolean | null;
  assigneeIds: string[] | null;
  recurrenceType: AgentEventRecurrenceType | null;
  /** "Monday" ... "Sunday" (Weekly). */
  recurrenceDaysOfWeek: string[] | null;
  recurrenceDayOfMonth: number | null;
  /** 1-5 (5 = last). */
  recurrenceWeekOfMonth: number | null;
  recurrenceDayOfWeekInTheMonth: string | null;
  /** "2026-12-28T23:59:59Z": last day, inclusive. */
  recurrenceEndDate: string | null;
  notificationTemplates: AgentReminder[] | null;
}

export interface CreateEventActionDto extends CreateResourceActionBaseDto {
  /** Empty for an open event. */
  assignees: AgentUserRef[];
  /** Complete create body. */
  payload: AgentEventPayload;
}

export interface UpdateEventActionDto extends UpdateResourceActionBaseDto {
  /** Only CHANGED values are set; `null` / missing means "unchanged" (removed values are in `clearedFields`). */
  payload: Partial<AgentEventPayload>;
}

export type AgentActionDto =
  | NavigateToAssetActionDto
  | NavigateToTicketActionDto
  | NavigateToEventActionDto
  | OpenDocumentActionDto
  | CreateAssetActionDto
  | UpdateAssetActionDto
  | CreateTicketActionDto
  | UpdateTicketActionDto
  | UpdateTicketStatusActionDto
  | CreateEventActionDto
  | UpdateEventActionDto
  | AgentActionBaseDto;

/**
 * What happened after the user picked a card: `saved` = the draft was persisted through its form, `opened` = a
 * navigation / document card did its job, `dismissed` = the form closed without saving.
 */
export type AgentActionOutcome = 'saved' | 'opened' | 'dismissed';

/**
 * Card family: `navigation` opens an existing item, `draft` opens a prefilled form, `status` changes a ticket
 * status in one click (the only action without a form).
 */
export type AgentCardKind = 'navigation' | 'draft' | 'status';

export type AgentCardPillTone = 'neutral' | 'warning' | 'success' | 'danger';

/** Small label under a card title (due date, ticket status). */
export interface AgentCardPill {
  label: string;
  tone: AgentCardPillTone;
}

/** One line of an edit card: field label, ~~old~~ -> new; `newValue` is `null` when the value is removed. */
export interface AgentCardChange {
  label: string;
  oldValue: string | null;
  newValue: string | null;
}

/** Action as rendered by the chat: a resolved type and display texts on top of the payload. */
export interface AgentChatAction {
  id: string;
  type: AgentActionType;
  kind: AgentCardKind;
  /** What the draft does ("New asset", "Edit task", "Status change"); `null` for navigation cards. */
  typeName: string | null;
  /** Name of the target (asset / document / ticket / event). */
  label: string;
  /** 12px line under the title, parts joined with " · "; `null` when there is nothing to add. */
  helper: string | null;
  pills: AgentCardPill[];
  /** Proposed changes of an edit card; empty otherwise. */
  changes: AgentCardChange[];
  /** Names of existing items a new draft may duplicate. */
  duplicates: string[];
  /** The target has a usable 3D position the camera can be moved to. */
  canGoToPosition: boolean;
  /** A draft card whose change has been saved; the chat shows it as done and offers no button. */
  completed: boolean;
  /** When the change was saved (ISO); `null` until then. */
  completedAt: string | null;
  dto: AgentActionDto;
}

/** Swagger declares the type as a number, but the API serializes enums by name ("NavigateToAsset"); both are accepted. */
export const normalizeActionType = (type: AgentActionBaseDto['type']): AgentActionType => {
  if (typeof type === 'number') return type in AgentActionType ? type : AgentActionType.Unknown;
  const name = `${type}`.trim();
  const byName = AgentActionType[(name.charAt(0).toUpperCase() + name.slice(1)) as keyof typeof AgentActionType];
  if (typeof byName === 'number') return byName;
  const byNumber = Number(name);
  return byNumber in AgentActionType ? byNumber : AgentActionType.Unknown;
};

const hasString = (action: object, key: string): boolean =>
  key in action && typeof (action as Record<string, unknown>)[key] === 'string';

const hasPayload = (action: object): boolean => {
  const { payload } = action as { payload?: unknown };
  return typeof payload === 'object' && payload !== null;
};

const hasArray = (action: object, key: string): boolean => Array.isArray((action as Record<string, unknown>)[key]);

/** Shape shared by every `Create*` draft card: the target space plus the list of possible duplicates. */
const isCreateDraftAction = (action: AgentActionDto): action is CreateResourceActionBaseDto =>
  hasString(action, 'apartmentId') && hasArray(action, 'similarResources') && hasPayload(action);

/** Shape shared by every `Update*` draft card: the current item plus the list of changes. */
const isUpdateDraftAction = (action: AgentActionDto): action is UpdateResourceActionBaseDto =>
  hasString(action, 'targetId') &&
  hasString(action, 'apartmentId') &&
  Array.isArray((action as Partial<UpdateResourceActionBaseDto>).changes) &&
  hasPayload(action);

const isNavigateToAction = (action: AgentActionDto): action is NavigateToActionBaseDto =>
  hasString(action, 'targetId') && hasString(action, 'apartmentId');

export const isNavigateToAssetAction = (action: AgentActionDto): action is NavigateToAssetActionDto =>
  normalizeActionType(action.type) === AgentActionType.NavigateToAsset && isNavigateToAction(action);

export const isNavigateToTicketAction = (action: AgentActionDto): action is NavigateToTicketActionDto =>
  normalizeActionType(action.type) === AgentActionType.NavigateToTicket && isNavigateToAction(action);

export const isNavigateToEventAction = (action: AgentActionDto): action is NavigateToEventActionDto =>
  normalizeActionType(action.type) === AgentActionType.NavigateToEvent && isNavigateToAction(action);

export const isOpenDocumentAction = (action: AgentActionDto): action is OpenDocumentActionDto =>
  normalizeActionType(action.type) === AgentActionType.OpenDocument && hasString(action, 'documentId');

export const isCreateAssetAction = (action: AgentActionDto): action is CreateAssetActionDto =>
  normalizeActionType(action.type) === AgentActionType.CreateAsset &&
  isCreateDraftAction(action) &&
  typeof (action as Partial<CreateAssetActionDto>).payload?.name === 'string';

export const isUpdateAssetAction = (action: AgentActionDto): action is UpdateAssetActionDto =>
  normalizeActionType(action.type) === AgentActionType.UpdateAsset &&
  hasString(action, 'targetId') &&
  hasString(action, 'apartmentId');

export const isCreateTicketAction = (action: AgentActionDto): action is CreateTicketActionDto =>
  normalizeActionType(action.type) === AgentActionType.CreateTicket &&
  isCreateDraftAction(action) &&
  hasArray(action, 'assignees');

export const isUpdateTicketAction = (action: AgentActionDto): action is UpdateTicketActionDto =>
  normalizeActionType(action.type) === AgentActionType.UpdateTicket && isUpdateDraftAction(action);

export const isUpdateTicketStatusAction = (action: AgentActionDto): action is UpdateTicketStatusActionDto => {
  if (normalizeActionType(action.type) !== AgentActionType.UpdateTicketStatus || !isUpdateDraftAction(action)) {
    return false;
  }
  const payload = (action as Partial<UpdateTicketStatusActionDto>).payload;
  return typeof payload?.ticketId === 'string' && typeof payload?.status === 'string';
};

export const isCreateEventAction = (action: AgentActionDto): action is CreateEventActionDto =>
  normalizeActionType(action.type) === AgentActionType.CreateEvent &&
  isCreateDraftAction(action) &&
  hasArray(action, 'assignees');

export const isUpdateEventAction = (action: AgentActionDto): action is UpdateEventActionDto =>
  normalizeActionType(action.type) === AgentActionType.UpdateEvent && isUpdateDraftAction(action);

/** `true` for the action types the client knows how to perform with a well-formed payload; unknown types are ignored. */
export const isSupportedAgentAction = (action: AgentActionDto): boolean =>
  isNavigateToAssetAction(action) ||
  isNavigateToTicketAction(action) ||
  isNavigateToEventAction(action) ||
  isOpenDocumentAction(action) ||
  isCreateAssetAction(action) ||
  isUpdateAssetAction(action) ||
  isCreateTicketAction(action) ||
  isUpdateTicketAction(action) ||
  isUpdateTicketStatusAction(action) ||
  isCreateEventAction(action) ||
  isUpdateEventAction(action);
